import { AlertCircle } from 'lucide-react';
import { usePets } from '../../context/PetsContext';

interface PetSelectionProps {
  selectedPets: string[];
  onSelectionChange: (pets: string[]) => void;
}

export function PetSelection({ selectedPets, onSelectionChange }: PetSelectionProps) {
  const { pets } = usePets();
  
  const togglePet = (petId: string) => {
    if (selectedPets.includes(petId)) {
      onSelectionChange(selectedPets.filter(id => id !== petId));
    } else {
      onSelectionChange([...selectedPets, petId]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2 dark:text-white">Select Pet(s) for Appointment</h3>
        <p className="text-gray-600 dark:text-white/70">Choose one or more pets (multi-select for boarding, grooming, etc.)</p>
      </div>

      <div className="space-y-3">
        {pets.map(pet => {
          const hasAlert = Math.random() > 0.7; // Mock alert logic
          
          return (
            <label
              key={pet.id}
              className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedPets.includes(pet.id)
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                  : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedPets.includes(pet.id)}
                onChange={() => togglePet(pet.id)}
                className="w-5 h-5 text-blue-600 rounded"
              />
              
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-full flex items-center justify-center text-3xl flex-shrink-0">
                {pet.species === 'dog' ? '🐕' : '🐈'}
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="dark:text-white">{pet.name}</h4>
                  {hasAlert && (
                    <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-xs">Vaccine due</span>
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 dark:text-white/60">{pet.breed} • {pet.gender}</div>
              </div>

              <div className="text-sm text-gray-500 dark:text-white/50">
                {new Date().getFullYear() - new Date(pet.dateOfBirth).getFullYear()} years old
              </div>
            </label>
          );
        })}
      </div>

      {selectedPets.length > 1 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>{selectedPets.length} pets selected.</strong> This is great for boarding, group grooming, or multi-pet services.
          </p>
        </div>
      )}
    </div>
  );
}